/**
 * @fileoverview ChatMessage - Premium chat message component
 *
 * Clean, borderless layout inspired by ChatGPT / Claude.
 * Assistant messages render full-width with no bubble — just clean typography.
 * User messages use a compact right-aligned bubble in Morgan green.
 * Tables render with strong contrast inside a white card.
 * Inline code that looks like a Convex document ID renders as a clickable button.
 */

import * as React from "react";
import { useMemo, useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/utils/misc";
import { Logo } from "@/ui/layout/logo";
import { FileText } from "lucide-react";
import { SourceChips } from "@/components/agents/universal/sourceCitations";
import {
  extractInlineSources,
  type CitedSource,
  type ContentSegment,
} from "@/components/agents/universal/sourceCitations.helpers";
import { ChartCard } from "@/components/agents/universal/chat/ChartCard";
import {
  DiagramActions,
  DiagramCard,
} from "@/components/agents/universal/chat/DiagramCard";
import { DiagramRenderer } from "@/components/agents/universal/diagramRenderer";
import { SpreadsheetCard } from "@/components/agents/universal/chat/SpreadsheetCard";
import type { ChartSpec } from "@cvx/lib/charts/spec";
import type { DiagramSpec } from "@cvx/lib/diagrams/spec";
import type { SpreadsheetSpec } from "@cvx/lib/spreadsheets/spec";
import { segmentMentions, splitAttachmentSuffix } from "./messageTags";

export interface ChatMessageData {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt?: number;
  isStreaming?: boolean;
  /** Charts emitted by the createChart tool on this assistant message. */
  charts?: ChartSpec[];
  /** Mermaid diagrams emitted by the createDiagram tool. */
  diagrams?: DiagramSpec[];
  /** Spreadsheets emitted by the createSpreadsheet tool. */
  spreadsheets?: SpreadsheetSpec[];
  /**
   * Arbitrary surface-specific content rendered below the message body
   * (e.g. the mail agent's inline draft cards).
   */
  extra?: React.ReactNode;
}

export interface ChatUserInfo {
  avatarUrl?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

/** A document that `@FileName` / `Attached:` chips can resolve to. */
export interface ChatTaggedDocument {
  id: string;
  name: string;
}

interface ChatMessageProps {
  message: ChatMessageData;
  userInfo?: ChatUserInfo;
  /**
   * Known documents for this chat's scope. User-message `@FileName`
   * mentions and `Attached:` footers render as chips; names found here
   * become clickable and fire `onDocumentClick`.
   */
  taggedDocuments?: ChatTaggedDocument[];
  onDocumentClick?: (documentId: string) => void;
  onSourceClick?: (source: CitedSource) => boolean | undefined;
  /** Save an inline chart into the current thread's canvas. */
  onSaveChart?: (spec: ChartSpec) => void | Promise<void>;
  /** Save an inline Mermaid diagram into the current thread's canvas. */
  onSaveDiagram?: (spec: DiagramSpec) => void | Promise<void>;
  /** Save an inline spreadsheet into the current thread's canvas. */
  onSaveSpreadsheet?: (spec: SpreadsheetSpec) => void | Promise<void>;
  className?: string;
}

/** Convex IDs are alphanumeric, typically 20-40 chars */
const CONVEX_ID_RE = /^[a-z0-9]{20,40}$/;

function isConvexId(text: string): boolean {
  return CONVEX_ID_RE.test(text);
}

/** Inline rendering for a ```mermaid fence the model emitted in prose. */
function MermaidFenceBlock({
  code,
  onSaveToCanvas,
}: {
  code: string;
  onSaveToCanvas?: (spec: DiagramSpec) => void | Promise<void>;
}) {
  const spec: DiagramSpec = { title: "Diagram", code };
  return (
    <div className="not-prose my-3 rounded-lg border border-border bg-background p-3 shadow-sm">
      <div className="mb-2 flex justify-end">
        <DiagramActions spec={spec} onSaveToCanvas={onSaveToCanvas} />
      </div>
      <DiagramRenderer code={code} />
    </div>
  );
}

/** True when a block-code element carries the ```mermaid fence language. */
function isMermaidCodeElement(child: React.ReactNode): boolean {
  return (
    React.isValidElement(child) &&
    typeof (child.props as { className?: string }).className === "string" &&
    (child.props as { className: string }).className.includes(
      "language-mermaid",
    )
  );
}

function buildMarkdownComponents(
  onDocumentClick?: (documentId: string) => void,
  mermaidOpts?: {
    /**
     * Render ```mermaid fences as live diagrams. Off while the message is
     * still streaming — a half-streamed fence stays a plain code block and
     * flips to a diagram when the message completes.
     */
    render: boolean;
    onSaveDiagram?: (spec: DiagramSpec) => void | Promise<void>;
  },
): React.ComponentProps<typeof ReactMarkdown>["components"] {
  return {
    // Unwrap <pre> around a mermaid fence so the diagram card isn't nested
    // inside preformatted text.
    pre: ({ children }) => {
      if (
        mermaidOpts?.render &&
        React.Children.toArray(children).some(isMermaidCodeElement)
      ) {
        return <>{children}</>;
      }
      return <pre>{children}</pre>;
    },
    table: ({ children }) => (
      <div className="not-prose my-3 overflow-hidden rounded-lg border border-border bg-background shadow-sm dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">{children}</table>
        </div>
      </div>
    ),
    thead: ({ children }) => {
      // Detect if all th cells are empty — if so, hide the header entirely.
      // LLMs sometimes output tables with blank header rows.
      const isEmpty = !React.Children.toArray(children).some((row) => {
        if (!React.isValidElement(row)) return false;
        return React.Children.toArray(
          (row as React.ReactElement<{ children?: React.ReactNode }>).props
            .children,
        ).some((cell) => {
          if (!React.isValidElement(cell)) return false;
          const cellChildren = (
            cell as React.ReactElement<{ children?: React.ReactNode }>
          ).props.children;
          if (cellChildren == null) return false;
          if (typeof cellChildren === "string")
            return cellChildren.trim().length > 0;
          return true;
        });
      });
      if (isEmpty) return null;
      return (
        <thead className="border-b-2 border-accent/30 bg-accent/20 dark:border-accent/25 dark:bg-accent/15">
          {children}
        </thead>
      );
    },
    th: ({ children }) => (
      <th className="whitespace-nowrap px-4 py-2.5 text-left text-xs font-semibold text-foreground/70 dark:text-gray-200">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border-b border-border/40 px-4 py-2.5 text-sm dark:border-gray-700/60 dark:text-gray-300">
        {children}
      </td>
    ),
    tr: ({ children }) => (
      <tr className="transition-colors hover:bg-muted/30">{children}</tr>
    ),
    blockquote: ({ children }) => (
      <blockquote className="my-2 border-l-2 border-accent pl-4 italic text-muted-foreground">
        {children}
      </blockquote>
    ),
    code: ({ children, className: codeClassName }) => {
      const isInline = !codeClassName;
      const text = String(children).trim();

      // Render Convex document IDs as clickable buttons
      if (isInline && isConvexId(text) && onDocumentClick) {
        return (
          <button
            type="button"
            onClick={() => onDocumentClick(text)}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-[hsl(128,38%,17%)]/20 bg-[hsl(128,38%,17%)]/5 px-2 py-0.5 align-middle text-xs font-medium text-[hsl(128,38%,17%)] transition-colors hover:border-[hsl(128,38%,17%)]/40 hover:bg-[hsl(128,38%,17%)]/10 dark:text-emerald-400"
          >
            <FileText className="h-3 w-3" />
            View Source Document
          </button>
        );
      }

      if (isInline) {
        return (
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground">
            {children}
          </code>
        );
      }
      if (mermaidOpts?.render && codeClassName?.includes("language-mermaid")) {
        return (
          <MermaidFenceBlock
            code={text}
            onSaveToCanvas={mermaidOpts.onSaveDiagram}
          />
        );
      }
      return (
        <code
          className={cn(
            "block overflow-x-auto rounded-lg bg-muted p-3 font-mono text-xs",
            codeClassName,
          )}
        >
          {children}
        </code>
      );
    },
  };
}

/**
 * Buffers streaming content to reduce re-render frequency.
 * Instead of re-parsing markdown on every token (~50/s), flushes at
 * fixed intervals (~12/s) for a smooth typing feel.
 */
function useBufferedContent(
  content: string,
  isStreaming: boolean,
  intervalMs = 80,
) {
  const [buffered, setBuffered] = useState(content);
  const latestRef = useRef(content);
  latestRef.current = content;

  useEffect(() => {
    if (!isStreaming) {
      setBuffered(content);
      return;
    }
    // Flush immediately on first content, then buffer
    setBuffered(content);
    const timer = setInterval(() => {
      setBuffered(latestRef.current);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [isStreaming, intervalMs, content]);

  return isStreaming ? buffered : content;
}

/** Memoized markdown renderer — avoids full re-parse for completed messages */
const MemoizedMarkdown = React.memo(function MemoizedMarkdown({
  content,
  components,
}: {
  content: string;
  components: React.ComponentProps<typeof ReactMarkdown>["components"];
}) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </ReactMarkdown>
  );
});

/**
 * Document chip inside the green user bubble. Resolvable names open the
 * document detail view; unresolved names (e.g. a doc later deleted)
 * still render as a chip, just not clickable.
 */
function UserDocumentChip({
  name,
  documentId,
  onDocumentClick,
}: {
  name: string;
  documentId?: string;
  onDocumentClick?: (documentId: string) => void;
}) {
  const clickable = !!documentId && !!onDocumentClick;
  const inner = (
    <>
      <FileText className="h-3 w-3 shrink-0 opacity-80" />
      <span className="max-w-[220px] truncate">{name}</span>
    </>
  );
  if (!clickable) {
    return (
      <span className="mx-0.5 inline-flex max-w-full items-center gap-1 rounded-md bg-white/15 px-1.5 py-0.5 align-middle text-xs font-medium text-white">
        {inner}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onDocumentClick(documentId)}
      title={`View ${name}`}
      className="mx-0.5 inline-flex max-w-full cursor-pointer items-center gap-1 rounded-md bg-white/15 px-1.5 py-0.5 align-middle text-xs font-medium text-white transition-colors hover:bg-white/30"
    >
      {inner}
    </button>
  );
}

export function ChatMessage({
  message,
  taggedDocuments,
  onDocumentClick,
  onSourceClick,
  onSaveChart,
  onSaveDiagram,
  onSaveSpreadsheet,
  className,
}: ChatMessageProps) {
  const isUser = message.role === "user";

  // Break the user message into prose, @-mention chips, and the
  // `Attached:` footer chips (MOR-890). Cheap no-ops for assistant turns.
  const taggedDocByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const doc of taggedDocuments ?? []) map.set(doc.name, doc.id);
    return map;
  }, [taggedDocuments]);
  const userTags = useMemo(() => {
    if (!isUser) return null;
    const { body, attachedNames } = splitAttachmentSuffix(message.content);
    return {
      attachedNames,
      segments: segmentMentions(body, [...taggedDocByName.keys()]),
    };
  }, [isUser, message.content, taggedDocByName]);

  const components = useMemo(
    () =>
      buildMarkdownComponents(onDocumentClick, {
        render: !message.isStreaming,
        onSaveDiagram,
      }),
    [onDocumentClick, message.isStreaming, onSaveDiagram],
  );

  // Buffer streaming content to ~12fps for smooth rendering
  const displayContent = useBufferedContent(
    message.content,
    message.isStreaming ?? false,
  );

  // Split assistant prose from the agent's `morgan-nav` citations so
  // the raw JSON never makes it into the rendered markdown. During
  // streaming, strip the incomplete fence as soon as we see the opening
  // marker so raw JSON never flashes in the UI.
  const segments: ContentSegment[] = useMemo(() => {
    if (message.role !== "assistant") {
      return [{ type: "prose", text: displayContent }];
    }
    if (message.isStreaming) {
      // Strip incomplete morgan-nav fence so raw JSON doesn't flash
      const lastFenceStart = displayContent.lastIndexOf("```morgan-nav");
      const closingFence =
        lastFenceStart !== -1
          ? displayContent.indexOf("```", lastFenceStart + 13)
          : -1;
      const cleaned =
        lastFenceStart !== -1 && closingFence === -1
          ? displayContent.slice(0, lastFenceStart).trimEnd()
          : displayContent;
      return extractInlineSources(cleaned);
    }
    return extractInlineSources(displayContent);
  }, [displayContent, message.role, message.isStreaming]);

  if (isUser) {
    return (
      <div className={cn("flex justify-end px-5 py-2", className)}>
        <div className="max-w-[75%]">
          <div className="whitespace-pre-wrap rounded-2xl rounded-tr-md bg-[hsl(128,38%,17%)] px-4 py-2.5 text-sm leading-relaxed text-white shadow-sm">
            {userTags
              ? userTags.segments.map((segment, i) =>
                  segment.type === "text" ? (
                    <React.Fragment key={i}>{segment.text}</React.Fragment>
                  ) : (
                    <UserDocumentChip
                      key={i}
                      name={segment.name}
                      documentId={taggedDocByName.get(segment.name)}
                      onDocumentClick={onDocumentClick}
                    />
                  ),
                )
              : message.content}
            {userTags && userTags.attachedNames.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-1 border-t border-white/15 pt-2">
                <span className="mr-0.5 text-[10px] font-medium uppercase tracking-wider text-white/60">
                  Attached
                </span>
                {userTags.attachedNames.map((name) => (
                  <UserDocumentChip
                    key={name}
                    name={name}
                    documentId={taggedDocByName.get(name)}
                    onDocumentClick={onDocumentClick}
                  />
                ))}
              </div>
            )}
          </div>
          {message.createdAt && (
            <p className="mr-1 mt-1 text-right text-[10px] tabular-nums text-muted-foreground/50">
              {new Date(message.createdAt).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Determine if sources are inline (interleaved) vs trailing-only
  const hasInlineSources = segments.some(
    (seg, i) => seg.type === "sources" && i < segments.length - 1,
  );

  // Assistant message — full-width, clean layout
  return (
    <div className={cn("flex gap-3 px-5 py-3", className)}>
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-background">
        <Logo compact width={20} height={20} className="text-primary" />
      </div>
      <div className="min-w-0 flex-1 pt-[5px]">
        {segments.map((segment, idx) => {
          if (segment.type === "prose") {
            const isLastProse =
              idx === segments.length - 1 ||
              (idx === segments.length - 2 &&
                segments[segments.length - 1].type === "sources");
            return (
              <div
                key={idx}
                className={cn(
                  "chat-prose prose prose-sm max-w-none text-sm leading-relaxed dark:prose-invert",
                  "prose-headings:mb-2 prose-headings:mt-4 prose-headings:font-semibold prose-headings:text-foreground prose-p:my-1.5 [&>p:first-child]:mt-0",
                  "prose-ol:my-1.5 prose-ul:my-1.5 prose-li:my-0.5 prose-hr:my-3",
                  "prose-a:text-emerald-700 prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground dark:prose-a:text-emerald-400",
                  message.isStreaming &&
                    isLastProse &&
                    "[will-change:contents]",
                )}
              >
                {message.isStreaming && isLastProse ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={components}
                  >
                    {segment.text}
                  </ReactMarkdown>
                ) : (
                  <MemoizedMarkdown
                    content={segment.text}
                    components={components}
                  />
                )}
                {message.isStreaming && isLastProse && (
                  <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse rounded-sm bg-foreground/70 align-middle" />
                )}
              </div>
            );
          }
          // Sources segment
          return (
            <SourceChips
              key={idx}
              sources={segment.sources}
              onSourceClick={onSourceClick}
              inline={hasInlineSources}
            />
          );
        })}
        {message.charts?.map((spec, i) => (
          <ChartCard
            key={`chart-${i}`}
            spec={spec}
            onSaveToCanvas={onSaveChart}
          />
        ))}
        {message.diagrams?.map((spec, i) => (
          <DiagramCard
            key={`diagram-${i}`}
            spec={spec}
            onSaveToCanvas={onSaveDiagram}
          />
        ))}
        {message.spreadsheets?.map((spec, i) => (
          <SpreadsheetCard
            key={`sheet-${i}`}
            spec={spec}
            onSaveToCanvas={onSaveSpreadsheet}
          />
        ))}
        {message.extra}
        {message.createdAt && !message.isStreaming && (
          <p className="ml-0.5 mt-1.5 text-[10px] tabular-nums text-muted-foreground/50">
            {new Date(message.createdAt).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        )}
      </div>
    </div>
  );
}
